import { useRef } from 'react';

import { useClickOutside } from '../../hooks/use-click-outside';
import type { MySkill } from '../../lib/api-client';

interface Props {
  /** Skills the current user is entitled to (already filtered to their grants). */
  available: MySkill[];
  status: 'idle' | 'loading' | 'success' | 'error';
  /** null = unset (all available enabled); array = explicit selection. */
  selected: string[] | null;
  onToggle: (skillId: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  /** Clear the per-conversation narrowing → back to "all my skills". */
  onReset: () => void;
  /** Fires on close (click-outside / done) so the ViewModel can persist. */
  onClose: () => void;
}

/**
 * The composer's per-conversation skill selector. Mirrors SlashCommandPicker's
 * floating-panel pattern (anchored above the composer). Lists ONLY the skills
 * the user is entitled to; checking/unchecking narrows which skills the agent
 * may use for this conversation. "Reset to all" clears the narrowing entirely.
 */
export function SkillSelectorPopover({
  available,
  status,
  selected,
  onToggle,
  onSelectAll,
  onSelectNone,
  onReset,
  onClose,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  useClickOutside(ref, onClose);

  // "all" when unset (null) — every available skill is implicitly enabled.
  const isAll = selected === null;
  const isChecked = (id: string) => (isAll ? true : selected!.includes(id));
  const enabledCount = isAll ? available.length : selected!.length;

  return (
    <div
      ref={ref}
      role="listbox"
      aria-label="Skills for this conversation"
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 6px)',
        left: 0,
        right: 0,
        maxHeight: 320,
        background: 'var(--surface-raise)',
        border: '1px solid var(--border-strong)',
        borderRadius: 10,
        boxShadow: 'var(--shadow-soft)',
        overflow: 'hidden',
        zIndex: 40,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 12px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-alt)',
          fontSize: 10,
          color: 'var(--text-mute)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontWeight: 600,
        }}
      >
        <span style={{ color: 'var(--accent)' }}>🧩</span>
        <span>Skills for this chat</span>
        <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: 'var(--text-soft)' }}>
          {enabledCount}/{available.length}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
          <button type="button" onClick={onSelectAll} style={linkBtn}>All</button>
          <button type="button" onClick={onSelectNone} style={linkBtn}>None</button>
          {!isAll && (
            <button type="button" onClick={onReset} style={{ ...linkBtn, color: 'var(--accent)' }}>
              Reset to all
            </button>
          )}
        </span>
      </div>

      <div style={{ overflowY: 'auto', maxHeight: 280, padding: 4 }}>
        {status === 'loading' && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>Loading skills…</div>
        )}
        {status === 'error' && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--kind-failure)' }}>Failed to load skills.</div>
        )}
        {(status === 'success' || status === 'idle') && available.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)', textAlign: 'center' }}>
            No skills assigned to you.
          </div>
        )}
        {available.map((skill) => {
          const checked = isChecked(skill.skill_id);
          return (
            <label
              key={skill.skill_id}
              role="option"
              aria-selected={checked}
              onMouseDown={(e) => {
                // mousedown + preventDefault: keep textarea focus and fire the
                // toggle before the document click-outside handler runs.
                e.preventDefault();
                onToggle(skill.skill_id);
              }}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '7px 12px',
                borderRadius: 6,
                cursor: 'pointer',
                background: checked ? 'var(--accent-soft)' : 'transparent',
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                readOnly
                tabIndex={-1}
                style={{ marginTop: 2, accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }}
              />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{skill.name}</span>
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--text-soft)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {skill.description}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

const linkBtn: React.CSSProperties = {
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  fontSize: 11,
  color: 'var(--text-soft)',
  fontFamily: 'inherit',
  padding: 0,
};
